// src/pages/WorkerDashboard.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import WorkerSidebar from "../components/WorkerSidebar";
import BidModal from "../components/BidModal";
import { GoogleMap, Marker, MarkerClusterer } from "@react-google-maps/api";
import haversine from "haversine-distance";
import socket from "../socket";
import { useNavigate } from "react-router-dom";

const API_WRK = process.env.REACT_APP_API_WRK;

const mapContainerStyle = {
  width: "100%",
  height: "400px",
};

const WorkerDashboard = () => {
  const [workerData, setWorkerData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBidModal, setShowBidModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showProfileReminder, setShowProfileReminder] = useState(false);
  const workerId = localStorage.getItem("workerId");
  const navigate = useNavigate();

  // ✅ Profile reminder check
  useEffect(() => {
    const seen = localStorage.getItem("publicProfileReminderSeen");
    if (!seen) setShowProfileReminder(true);
  }, []);

  const handleDismissReminder = () => {
    localStorage.setItem("publicProfileReminderSeen", "true");
    setShowProfileReminder(false);
    navigate("/public-profile");
  };

  // ✅ Fetch worker + jobs
  const fetchData = async () => {
    if (!workerId) return;
    try {
      const res = await axios.get(`${API_WRK}/profile/${workerId}`);
      const profile = res.data;
      setWorkerData(profile);

      const jobsRes = await axios.get(`${API_WRK}/available-jobs/${workerId}`);
      const allJobs = jobsRes.data || [];

      if (profile.latitude && profile.longitude) {
        const filtered = allJobs.filter((job) => {
          if (!job.latitude || !job.longitude) return false;
          const workerLocation = { lat: profile.latitude, lng: profile.longitude };
          const jobLocation = { lat: job.latitude, lng: job.longitude };
          const distanceKm = haversine(workerLocation, jobLocation) / 1000;
          return distanceKm <= 150;
        });
        setJobs(filtered);
      } else {
        setJobs(allJobs);
      }
    } catch (err) {
      console.error("Failed to load dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     ⚡ SOCKET: Real-time job updates
  ============================================================ */
  useEffect(() => {
    if (!workerId) {
      navigate("/worker-login");
      return;
    }

    fetchData(); // initial load

    // ✅ Register worker and join service rooms
    const joinSocketRooms = async () => {
      try {
        const res = await axios.get(`${API_WRK}/profile/${workerId}`);
        const profile = res.data;
        const services = (profile.services || []).map((s) => s.name);
        socket.emit("registerWorker", { workerId, services });
      } catch (err) {
        console.error("❌ Failed to join service rooms:", err);
      }
    };
    joinSocketRooms();

    // ✅ Listen for new job broadcasts
    socket.on("job:new", (job) => {
      console.log("🚨 New job received via socket:", job);
      setJobs((prev) => {
        if (prev.some((j) => j._id === job._id)) return prev; // avoid duplicates

        // Filter by distance if location available
        if (
          workerData?.latitude &&
          workerData?.longitude &&
          job.latitude &&
          job.longitude
        ) {
          const dist = haversine(
            { lat: workerData.latitude, lng: workerData.longitude },
            { lat: job.latitude, lng: job.longitude }
          ) / 1000;
          if (dist > 150) return prev;
        }

        return [job, ...prev];
      });
    });

    // ✅ Optional: Listen for job assignment or rejection
    socket.on("job:update", (update) => {
      console.log("📦 Job status update:", update);
      if (update.status === "assigned" || update.status === "rejected") {
        // You can trigger a refresh or toast here
        fetchData();
      }
    });

    return () => {
      socket.off("job:new");
      socket.off("job:update");
    };
  }, [workerId, navigate, workerData]);

  // ✅ Rejected & incomplete checks
  const getRejectedItems = () => {
    if (!workerData) return [];
    const rejected = [];
    if (workerData.profilePhotoStatus === "rejected") rejected.push("Profile Photo");
    if (workerData.selfieStatus === "rejected") rejected.push("Selfie (ID Verification)");
    if (workerData.rideLicenseStatus === "rejected") rejected.push("Ride License");
    if (workerData.rideInsuranceStatus === "rejected") rejected.push("Ride Insurance");
    if (workerData.vehicleRegistrationStatus === "rejected")
      rejected.push("Vehicle Registration");
    if (workerData.permitStatus === "rejected") rejected.push("Work/Study Permit");
    workerData.services?.forEach((s) => {
      if (s.certStatus === "rejected") rejected.push(`Certificate: ${s.name}`);
    });
    return rejected;
  };

  const getIncompleteItems = () => {
    if (!workerData) return [];
    const incomplete = [];
    if (
      !workerData.profilePhotoUrl ||
      workerData.profilePhotoUrl === "null" ||
      workerData.profilePhotoUrl === "undefined"
    ) {
      incomplete.push("Profile Picture");
    }
    if (workerData.termsAccepted !== true) incomplete.push("Terms & Conditions");
    if (!workerData.selfieUrl) incomplete.push("Selfie (ID Verification)");
    workerData.services?.forEach((s) => {
      if (s.certRequired && !s.certUrl) {
        incomplete.push(`Certificate: ${s.name}`);
      }
    });
    return incomplete;
  };

  const rejectedItems = getRejectedItems();
  const incompleteItems = getIncompleteItems();

  if (loading) return <div className="p-6 text-lg">Loading dashboard...</div>;

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <WorkerSidebar
        handleLogout={() => {
          localStorage.clear();
          navigate("/worker-login");
        }}
      />

      <main className="flex-1 px-4 md:px-10 py-6 max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">
            Welcome, {workerData?.name || "Worker"}
          </h1>
          <p className="text-sm text-gray-500 italic mt-1">
            "Great work starts with great readiness."
          </p>
        </div>

        {/* ✨ Public Profile Reminder */}
        {showProfileReminder && (
          <div className="bg-indigo-50 border border-indigo-300 text-indigo-900 px-5 py-4 rounded-lg mb-6 shadow-sm">
            <p className="font-semibold mb-2 text-lg">
              ✨ Make your public profile stand out!
            </p>
            <p className="text-sm mb-3">
              Add your previous work, skills, and photos to attract more customers.
            </p>
            <button
              onClick={handleDismissReminder}
              className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 text-sm"
            >
              Go to My Public Profile
            </button>
          </div>
        )}

        {rejectedItems.length > 0 && (
          <div className="bg-red-50 border border-red-300 text-red-800 px-5 py-4 rounded-lg mb-6">
            <p className="font-semibold mb-2">Some of your documents were rejected:</p>
            <ul className="list-disc pl-6 text-sm mb-3">
              {rejectedItems.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <button
              onClick={() => navigate("/documents")}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm"
            >
              Fix Rejected Documents
            </button>
          </div>
        )}

        {incompleteItems.length > 0 && (
          <div className="bg-orange-50 border border-orange-300 text-orange-900 px-5 py-4 rounded-lg mb-6">
            <p className="font-semibold mb-2">
              Your profile is incomplete. Please complete the following:
            </p>
            <ul className="list-disc pl-6 text-sm mb-3">
              {incompleteItems.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <button
              onClick={() => navigate("/documents")}
              className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 text-sm"
            >
              Complete Your Profile
            </button>
          </div>
        )}

        {workerData?.status !== "approved" && (
          <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 px-5 py-4 rounded-lg mb-6">
            <p className="font-semibold mb-2">
              Your profile is still under review. You can view jobs but cannot accept them yet.
            </p>
          </div>
        )}

        {/* 🗺️ Map View */}
        <div className="rounded-lg overflow-hidden shadow mb-6">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            zoom={11}
            center={{
              lat: workerData?.latitude || 49.2827,
              lng: workerData?.longitude || -123.1207,
            }}
          >
            <MarkerClusterer>
              {(clusterer) =>
                jobs.map(
                  (job) =>
                    job.latitude &&
                    job.longitude && (
                      <Marker
                        key={job._id}
                        position={{ lat: job.latitude, lng: job.longitude }}
                        clusterer={clusterer}
                      />
                    )
                )
              }
            </MarkerClusterer>
          </GoogleMap>
        </div>

        {/* 📋 Available Jobs List */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">
            Available Jobs Near You (Within 150km)
          </h2>

          {jobs.length > 0 ? (
            <div className="space-y-4">
              {jobs.map((job) => (
                <div
                  key={job._id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition bg-gray-50 relative"
                >
                  <div className="absolute top-4 right-4 text-blue-700 text-2xl font-bold">
                    ${job.budget}
                  </div>

                  <div className="flex flex-col">
                    <h3 className="text-gray-900 font-semibold text-xl mb-2">
                      {job.jobTitle}
                    </h3>
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-blue-700">City:</span>{" "}
                      {job.city || "Not specified"}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-blue-700">Scheduled:</span>{" "}
                      {job.scheduledAt
                        ? new Date(job.scheduledAt).toLocaleString()
                        : "Not specified"}
                    </p>
                    {job.description && (
                      <p className="text-sm text-gray-600 mt-2">{job.description}</p>
                    )}

                    <div className="mt-4 text-right">
                      {workerData?.status === "approved" &&
                        (job.hasBidByThisWorker ? (
                          <span className="inline-block bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-sm">
                            Bid Submitted
                          </span>
                        ) : job.canBid ? (
                          <button
                            onClick={() => {
                              setSelectedJob(job);
                              setShowBidModal(true);
                            }}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                          >
                            Submit Bid
                          </button>
                        ) : (
                          <span className="inline-block bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">
                            Certificate Pending
                          </span>
                        ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">
              No jobs available in your area right now.
            </p>
          )}
        </div>

        {showBidModal && selectedJob && (
          <BidModal
            job={selectedJob}
            workerId={workerId}
            onClose={() => {
              setShowBidModal(false);
              setSelectedJob(null);
              fetchData();
            }}
          />
        )}
      </main>
    </div>
  );
};

export default WorkerDashboard;
